import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaExchangeAlt, FaSync } from 'react-icons/fa';
import { getCurrencyDisplayName, getShortLabel } from '../utils/currencyHelper';

// Türkçe sayı formatı: 1.000.000,50
const formatTurkishNumber = (num) => {
  if (num === 0 || num === '0') return '0';

  const number = parseFloat(num);
  if (isNaN(number)) return num;

  // Ondalık kısmı ayır
  const [intPart, decPart] = number.toFixed(2).split('.');

  // Binlik ayracı ile formatla
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  // Kuruş varsa virgülle ekle
  if (decPart === '00') {
    return formattedInt;
  }
  return `${formattedInt},${decPart}`;
};

const CurrencyTool = ({ compact = false, showTitle = true, defaultFrom = 'USD', defaultTo = 'TRY' }) => {
  const [currencies, setCurrencies] = useState([]);
  const [fromCurrency, setFromCurrency] = useState(defaultFrom);
  const [toCurrency, setToCurrency] = useState(defaultTo);
  const [amount, setAmount] = useState(1);
  const [result, setResult] = useState(0);

  const fetchCurrencies = React.useCallback(async () => {
    try {
      const { data } = await axios.get('/api/currency');
      setCurrencies(data);
    } catch (error) {
      console.error('Döviz kurları yüklenemedi:', error);
    }
  }, []);

  const calculateConversion = React.useCallback(() => {
    if (fromCurrency === 'TRY') {
      // TL'den dövize
      const toCurr = currencies.find(c => c.code === toCurrency);
      if (toCurr) {
        setResult((amount / toCurr.sellPrice).toFixed(4));
      }
    } else if (toCurrency === 'TRY') {
      // Dövizden TL'ye
      const fromCurr = currencies.find(c => c.code === fromCurrency);
      if (fromCurr) {
        setResult((amount * fromCurr.buyPrice).toFixed(2));
      }
    } else {
      // Dövizden dövize
      const fromCurr = currencies.find(c => c.code === fromCurrency);
      const toCurr = currencies.find(c => c.code === toCurrency);
      if (fromCurr && toCurr) {
        const tlValue = amount * fromCurr.buyPrice;
        setResult((tlValue / toCurr.sellPrice).toFixed(4));
      }
    }
  }, [amount, fromCurrency, toCurrency, currencies]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  useEffect(() => {
    calculateConversion();
  }, [calculateConversion]);

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const currencyOptions = [
    { code: 'TRY', name: 'Türk Lirası' },
    ...currencies
      .filter(c =>
        (c.category === 'currency' || c.category === 'gold' || c.category === 'silver' || !c.category) &&
        !c.code.includes('/') // Sadece pariteleri hariç tut
      )
      .map(c => ({ code: c.code, name: getCurrencyDisplayName(c.code) }))
  ];

  return (
    <div className={`w-full min-w-0 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 transition-colors duration-300 ease-in-out ${compact ? 'p-4 sm:p-5' : 'p-4 sm:p-6 lg:p-8'}`}>
      {showTitle && (
        <div className={`${compact ? 'mb-5 text-left' : 'mb-6 sm:mb-8 text-center'}`}>
          <h3 className={`text-lg sm:text-xl font-bold flex items-center ${compact ? 'justify-start' : 'justify-center'} space-x-3 text-gray-900 dark:text-white`}>
            <span className="bg-indigo-50 dark:bg-indigo-900/30 p-2.5 rounded-xl transition-colors duration-300 ease-in-out">
              <FaExchangeAlt className="text-xl sm:text-2xl text-indigo-600 dark:text-indigo-400" />
            </span>
            <span>{compact ? 'Hızlı Döviz Çevirici' : 'Döviz Çevirici'}</span>
          </h3>
          {compact && <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Portföyünüzü incelerken anlık dönüşüm yapın.</p>}
        </div>
      )}

      <div className={compact ? 'space-y-4' : 'space-y-6'}>
        {/* 1. Satır: Kaynak/Hedef Kur ve Swap */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          {/* Kaynak Kur */}
          <select
            value={fromCurrency}
            onChange={(e) => setFromCurrency(e.target.value)}
            className="flex-1 min-w-0 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3.5 text-gray-900 dark:text-white text-base font-medium focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-colors duration-300 ease-in-out cursor-pointer appearance-none text-center"
          >
            {currencyOptions.map(curr => (
              <option key={curr.code} value={curr.code} className="text-gray-900">
                {curr.name}
              </option>
            ))}
          </select>

          {/* Swap Button (Yuvarlak) */}
          <button
            onClick={swapCurrencies}
            className="self-center flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 flex items-center justify-center transition-[transform,background-color,border-color,color,box-shadow] duration-300 ease-in-out hover:scale-110 border border-indigo-100 dark:border-indigo-800 group"
            title="Dövizleri değiştir"
          >
            <FaSync className="text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 rotate-90 sm:rotate-0 sm:group-hover:rotate-180 transition-transform" />
          </button>

          {/* Hedef Kur */}
          <select
            value={toCurrency}
            onChange={(e) => setToCurrency(e.target.value)}
            className="flex-1 min-w-0 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3.5 text-gray-900 dark:text-white text-base font-medium focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-colors duration-300 ease-in-out cursor-pointer appearance-none text-center"
          >
            {currencyOptions.map(curr => (
              <option key={curr.code} value={curr.code} className="text-gray-900">
                {curr.name}
              </option>
            ))}
          </select>
        </div>

        {/* 2. Satır: Miktar Girişi (Full Width) */}
        <div className="relative w-full">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={`w-full min-w-0 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl px-3 sm:px-4 pr-14 sm:pr-16 text-gray-900 dark:text-white font-bold text-center placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-indigo-50 focus:border-indigo-300 transition-[background-color,border-color,color,box-shadow] duration-300 ease-in-out shadow-inner ${compact ? 'py-3 text-xl sm:text-2xl' : 'py-4 sm:py-5 text-2xl sm:text-3xl'}`}
            placeholder="Miktar..."
            min="0"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-6 pointer-events-none">
            <span className="text-gray-400 dark:text-gray-500 font-bold text-xl">
              {getShortLabel(fromCurrency)}
            </span>
          </div>
        </div>

        {/* 3. Satır: Sonuç Gösterimi (GRADIENT) */}
        <div className={`min-w-0 bg-gradient-to-r from-indigo-600 to-blue-500 rounded-2xl text-center shadow-lg transform transition-transform sm:hover:scale-[1.02] overflow-hidden ${compact ? 'p-4' : 'p-4 sm:p-6'}`}>
          <div className="text-sm text-indigo-100 font-medium mb-2 uppercase tracking-wide">Hesaplanan Tutar</div>

          <div className="flex flex-wrap items-baseline justify-center gap-1 sm:gap-2 min-w-0">
            <span className={`max-w-full break-all font-extrabold text-white tracking-tight drop-shadow-sm ${compact ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl md:text-5xl'}`}>
              {formatTurkishNumber(result)}
            </span>
            <span className="text-base sm:text-xl md:text-2xl text-indigo-200 font-bold">
              {toCurrency === 'TRY' ? 'TRY' : getShortLabel(toCurrency)}
            </span>
          </div>

          <div className="text-xs mt-4 text-indigo-200 font-medium">
            1 {getShortLabel(fromCurrency)} = {currencies.find(c => c.code === fromCurrency)?.sellPrice || '-'} TRY
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrencyTool;
